import { createArweaveUrl } from "@hl/shared-features/lib/utils/arweave";
import {
  buildContractUrl,
  networkLookup,
  NetworkTypeData,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { viewCollectionOpenSeaUrl } from "@hl/shared-features/lib/utils/opensea";

import {
  _CollectionType,
  Auction,
  AuctionStatus,
  CollectionContractType,
  Contract2,
  ContractEntityType,
  Maybe,
  MergedCollection,
  MergedDraft,
  MintVector,
  MintVectorStatus,
  NftContractStandard,
  PriceType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { GetDashboardCollectionsQuery } from "~/features/collections-dashboard/queries.graphql.generated";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

interface Details {
  name: string;
  image?: string;
  nonTransferable?: boolean;
}

export enum BlockchainEntityType {
  COLLECTION = "COLLECTION",
  TOKEN = "TOKEN",
}

export enum BlockchainEntityStatus {
  DRAFT,
  DEPLOYING,
  IMPORTED,
  MINT_STARTED,
  MINT_PAUSED,
  MINT_ENDED,
  MINT_NOT_STARTED,
  MINT_SOLD_OUT,
  AUCTION_NOT_STARTED,
  AUCTION_IN_PROGRESS,
  AUCTION_ENDED_NEEDS_FULFILLMENT,
  AUCTION_ENDED_FULFILLED,
  AUCTION_ENDED_RESERVE_NOT_MET,
}

interface MintStatus {
  minted: number;
  total?: number;
}

export type BlockchainEntity = {
  id: string;
  onchainId: string | null;
  details: Details;
  address?: string;
  chainId?: number;
  contractType?: string;
  contractStandard?: NftContractStandard;
  entityType: BlockchainEntityType;
  status?: BlockchainEntityStatus;
  openSeaUrl?: string | null;
  blockExplorerUrl?: string | null;
  arweaveUrl?: string | null;
  publicPrice?: string;
  priceType?: PriceType;
  start?: string | null;
  end?: string | null;
  createdAt?: string;
  type: "mint" | "auction" | "imported";
  mintStatus?: MintStatus;
  collectionType?: _CollectionType;
  networkData?: NetworkTypeData | null;
  isDraft: boolean;
  isArchive: boolean;
  isReveal?: boolean;
  editionId?: string | null;
};

type BlockchainCollection = Partial<
  Omit<
    MergedCollection,
    | "mintVectors"
    | "auctions"
    | "contract"
    | "seriesTokens"
    | "generativeDetails"
    | "genSeriesHashes"
    | "flagVariations"
  >
> & {
  mintVectors?: Array<Partial<MintVector>>;
  auctions?: Array<Partial<Auction>>;
  contract?: Maybe<Partial<Contract2>>;
  seriesTokens?: GetManageTokenCollectionQuery["getCollection"]["seriesTokens"];
  generativeDetails?: GetManageTokenCollectionQuery["getCollection"]["generativeDetails"];
};

type BlockchainDraftCollection = Partial<MergedDraft> & {
  onchainId?: string | null;
};

type BlockchainCollectionWithDraft =
  | BlockchainCollection
  | BlockchainDraftCollection;

const isMergedCollectionDraft = (
  toBeDefined?: BlockchainCollectionWithDraft
): toBeDefined is BlockchainDraftCollection =>
  !!(toBeDefined as BlockchainDraftCollection).isDraft;

export const mapCollectionToBlockchainEntity = (
  collection: BlockchainCollection | BlockchainDraftCollection
): BlockchainEntity => {
  let details = {
    name: "",
    image: "",
    onChainImage: "",
    nonTransferable: false,
  };
  let status: BlockchainEntityStatus | undefined;
  let mintStatus: MintStatus | undefined;

  if (isMergedCollectionDraft(collection)) {
    const contractStandard =
      collection.contractDraft?.contractStandard ?? NftContractStandard.ERC721;
    const contractType =
      contractStandard === NftContractStandard.ERC1155 ? "ERC-1155" : "ERC-721";

    return {
      id: collection.id!,
      onchainId: collection.onchainId ?? null,
      type:
        collection.distributionDraft?.type === TokenDistributionType.Auction
          ? "auction"
          : "mint",
      address: "",
      chainId: collection.contractDraft?.chainId || undefined,
      entityType: BlockchainEntityType.COLLECTION,
      contractType,
      contractStandard,
      details: {
        name: collection.collectionDraft?.name || "",
        image:
          (collection.collectionType === _CollectionType.Series ||
          collection.collectionType === _CollectionType.GenerativeSeries
            ? collection.contractDraft?.collectionLogoImage
            : collection.metadata?.mediaUrl) || "",
        nonTransferable: !!collection.moduleSettings?.nonTransferable,
      },
      status: BlockchainEntityStatus.DRAFT,
      start: collection.distributionDraft?.saleDateFrom,
      end: collection.distributionDraft?.saleDateTo,
      createdAt: collection.createdAt,
      collectionType: collection.collectionType,
      networkData: collection.contractDraft?.chainId
        ? networkLookup(collection.contractDraft?.chainId)
        : null,
      isDraft: true,
      isArchive: !!collection.archivedAt,
      isReveal: !!collection.metadata?.reveal,
    };
  }
  const contractStandard =
    collection.contract?.contractType === ContractEntityType.EditionsDFS1155
      ? NftContractStandard.ERC1155
      : NftContractStandard.ERC721;
  let contractType =
    collection.contract?.contractType === ContractEntityType.EditionsDFS1155
      ? "ERC-1155"
      : "ERC-721";
  let openSeaUrl;
  const chainId = collection.chainId;
  const address = collection.address;
  const networkData = chainId ? networkLookup(chainId) : null;

  if (networkData && address && chainId) {
    openSeaUrl =
      collection.openSeaCollectionUrl ??
      viewCollectionOpenSeaUrl(address, chainId);
  }

  const blockExplorerUrl =
    networkData && address ? buildContractUrl(networkData.type, address) : null;

  details.name = collection.name || "";
  if (collection.editions?.length) {
    details.image = collection.editions[0].image;
    details.onChainImage = collection.editions[0].onChainImage;
    details.nonTransferable = collection.editions[0].nonTransferable ?? false;
  }
  if (collection.tokens?.length) {
    details.image = collection.tokens[0].image;
    details.onChainImage = collection.tokens[0].image;
    details.nonTransferable = collection.tokens[0].nonTransferable ?? false;
  }
  if (collection.collectionType === _CollectionType.GenerativeSeries) {
    if (collection.generativeDetails?.logoUrl) {
      details.image = collection.generativeDetails?.logoUrl;
    }
    details.nonTransferable = collection.nonTransferable ?? false;
  }
  if (collection.collectionType === _CollectionType.Series) {
    if (collection.seriesDetails?.logoUrl) {
      details.image = collection.seriesDetails?.logoUrl;
    } else if (collection.seriesImages?.coverImageUrl) {
      details.image = collection.seriesImages?.coverImageUrl;
    } else {
      const coverIndex = collection.seriesDetails?.coverImage ?? 1;
      if (
        collection.seriesTokens?.edges &&
        collection.seriesTokens?.edges.length >= coverIndex
      ) {
        details.image =
          collection.seriesTokens?.edges[coverIndex - 1].imageUrl ?? "";
      } else {
        details.image = "";
      }
    }
    details.nonTransferable = collection.nonTransferable ?? false;
  }

  const arweaveUrl = createArweaveUrl({
    collectionType: collection.collectionType,
    genSeriesMirroredBasedUri:
      collection.generativeDetails?.mirroredBaseUri || "",
    isReveal: !!collection.reveal,
    seriesMirroredBaseUri: collection.seriesDetails?.mirroredBaseUri || "",
    onChainBaseUri: collection.onChainBaseUri || "",
    onChainImage: details.onChainImage,
    editionOnchainMetadata:
      collection.edition?.onChainAnimation ||
      collection.edition?.onChainImage ||
      collection.edition?.onChainMetadata ||
      undefined,
  });

  const mintVector = collection.mintVectors?.length
    ? collection.mintVectors[0]
    : null;
  const auction = collection.auctions?.length ? collection.auctions[0] : null;

  if (mintVector) {
    status = getStatusFromMintVector(mintVector);
    mintStatus = {
      minted: collection.supply ?? (mintVector.stats?.sold || 0),
      total: collection.size ?? mintVector.stats?.total,
    };
  } else if (auction) {
    status = getStatusFromAuction(auction);
  }

  if (collection?.type === CollectionContractType.Imported) {
    details = {
      name: collection?.name || "",
      image: collection?.importData?.media || "",
      onChainImage: "",
      nonTransferable: false,
    };
    status = BlockchainEntityStatus.IMPORTED;
    contractType = collection.importData?.standard || "Unknown";
  }

  if (collection.status === "IN_PROGRESS") {
    status = BlockchainEntityStatus.DEPLOYING;
  }
  return {
    id: collection.id!,
    onchainId: collection.onchainId ?? null,
    type: mintVector ? "mint" : "auction",
    address: collection.contract?.address ?? collection.address ?? "",
    chainId: collection.chainId!,
    entityType: BlockchainEntityType.COLLECTION,
    contractType,
    contractStandard: contractStandard,
    openSeaUrl,
    blockExplorerUrl,
    arweaveUrl,
    details: {
      name: details?.name,
      image: details?.image,
      nonTransferable: !!details?.nonTransferable,
    },
    status,
    mintStatus,
    publicPrice:
      mintVector && !mintVector.gateId ? mintVector?.price : undefined,
    priceType: mintVector?.priceType ?? undefined,
    start: mintVector?.start || auction?.start,
    end: mintVector?.end || auction?.end,
    createdAt: collection.createdAt,
    collectionType: collection.collectionType,
    networkData,
    isDraft: false,
    isArchive: !!collection.archivedAt,
    isReveal: !!collection.reveal,
    editionId: collection?.editionId,
  };
};

const getStatusFromMintVector = (
  mintVector: Partial<MintVector>,
  collectionType?: _CollectionType
) => {
  if (
    mintVector.stats?.sold === mintVector.stats?.total &&
    collectionType &&
    collectionType !== _CollectionType.OpenEdition
  ) {
    return BlockchainEntityStatus.MINT_SOLD_OUT;
  }
  if (MintVectorStatus.Ended === mintVector.stats?.status) {
    return BlockchainEntityStatus.MINT_ENDED;
  }
  if (mintVector.paused) {
    return BlockchainEntityStatus.MINT_PAUSED;
  }
  if (MintVectorStatus.NotStarted === mintVector.stats?.status) {
    return BlockchainEntityStatus.MINT_NOT_STARTED;
  }

  return BlockchainEntityStatus.MINT_STARTED;
};

const getStatusFromAuction = (auction: Partial<Auction>) => {
  const auctionStatus = auction.stats?.status;
  switch (auctionStatus) {
    case AuctionStatus.InProgress:
      return BlockchainEntityStatus.AUCTION_IN_PROGRESS;
    case AuctionStatus.Ended:
      return auction.stats?.totalBids
        ? BlockchainEntityStatus.AUCTION_ENDED_NEEDS_FULFILLMENT
        : BlockchainEntityStatus.AUCTION_ENDED_RESERVE_NOT_MET;
    case AuctionStatus.Fulfilled:
      return BlockchainEntityStatus.AUCTION_ENDED_FULFILLED;
    default:
      return BlockchainEntityStatus.AUCTION_NOT_STARTED;
  }
};

export const mapDashboardCollectionToEntity = (
  collection: GetDashboardCollectionsQuery["getDashboardCollections"]["collections"][0]
): BlockchainEntity => {
  return {
    id: collection.collectionId,
    // TODO: figure out how to get onchainId here
    onchainId: null,
    address: collection.collectionAddress,
    arweaveUrl: undefined,
    blockExplorerUrl: undefined,
    chainId: collection.chainId,
    collectionType: collection.type,
    contractType: "",
    createdAt: collection.startDate,
    details: {
      name: collection.name,
      image: collection.logo,
    },
    editionId: collection.editionId,
    end: collection.endDate,
    entityType: BlockchainEntityType.COLLECTION,
    isArchive: collection.status === "Archived",
    isDraft: collection.status === "Draft",
    start: collection.startDate,
    status: BlockchainEntityStatus.MINT_STARTED,
    type: "mint",
  };
};
